import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Alert,
  Grid,
  Avatar,
  Button,
  useTheme,
  alpha,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  VpnKey as VpnKeyIcon,
  Numbers as NumbersIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { StyledButton } from '../../styles/SharedStyles';
import { validateInvoicePrefix } from '../../utils/validation';

interface ProfileFormData {
  username: string;
  email?: string;
  branch: string;
  role: string;
  mobile_no?: string;
  avatar_url?: string;
  invoice_prefix?: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ProfileManagement = () => {
  const { user, userProfile } = useAuth();
  const [profileData, setProfileData] = useState<ProfileFormData>({
    username: '',
    email: '',
    branch: '',
    role: '',
    mobile_no: '',
    avatar_url: '',
    invoice_prefix: '',
  });
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (userProfile) {
      setProfileData({
        username: userProfile.username || '',
        email: userProfile.email || '',
        branch: userProfile.profile_branches && Array.isArray(userProfile.profile_branches)
          ? userProfile.profile_branches
              .map(pb => pb.branch?.name || pb.branch_id)
              .join(', ')
          : '',
        role: userProfile.role || '',
        mobile_no: userProfile.mobile_no || '',
        avatar_url: '', // Will be filled from user_avatars table
        invoice_prefix: userProfile.invoice_prefix || '',
      });
      fetchAvatarForUser(userProfile.id);
    } else if (user) {
      fetchProfile();
    }
  }, [userProfile, user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setProfileData({
        username: data.username || '',
        email: user.email || '',
        branch: data.branch || '',
        role: data.role || '',
        mobile_no: data.mobile_no || '',
        avatar_url: '', // Will be filled from user_avatars table
        invoice_prefix: data.invoice_prefix || '',
      });
    } catch (error: any) {
      setError(error.message);
    }
  };

  const fetchAvatarForUser = async (profileId: string) => {
    const { data, error } = await supabase
      .from('user_avatars')
      .select('avatar_url')
      .eq('profile_id', profileId)
      .maybeSingle();
    if (!error && data) {
      setProfileData(prev => ({ ...prev, avatar_url: data.avatar_url }));
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Always update mobile_no as well as username
      const updateData: any = { 
         username: profileData.username,
         mobile_no: profileData.mobile_no 
      };

      if (userProfile && userProfile.role === 'admin') {
        updateData.invoice_prefix = profileData.invoice_prefix;
        updateData.branch = profileData.branch;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user?.id);

      if (error) throw error;

      setSuccess('Profile updated successfully');
      // Notify other components (e.g. UsersForm) to re-fetch updated data
      window.dispatchEvent(new Event('profile-updated'));
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setSuccess('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      setError(null);
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      // Upload the file to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      // Obtain the public URL for the uploaded avatar file
      const { data: publicUrlData } = supabase
        .storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Upsert the new avatar URL in the user_avatars table
      const { data: avatarData } = await supabase
        .from('user_avatars')
        .select('*')
        .eq('profile_id', user?.id)
        .maybeSingle();

      if (avatarData) {
        // Update existing row
        const { error: updateError } = await supabase
          .from('user_avatars')
          .update({ avatar_url: publicUrlData.publicUrl })
          .eq('profile_id', user?.id);
        if (updateError) throw updateError;
      } else {
        // Insert new row
        const { error: insertError } = await supabase
          .from('user_avatars')
          .insert([{ profile_id: user?.id, avatar_url: publicUrlData.publicUrl }]);
        if (insertError) throw insertError;
      }
      
      // Update local state
      setProfileData(prev => ({ ...prev, avatar_url: publicUrlData.publicUrl }));
      // Force refresh of profile and sidebar if needed
      await fetchProfile();
      window.dispatchEvent(new Event('profile-updated'));
      setSuccess('Profile photo updated successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setError(null);

      // Remove avatar URL from profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setProfileData(prev => ({
        ...prev,
        avatar_url: undefined
      }));

      setSuccess('Profile photo removed successfully');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleInvoicePrefixChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const prefix = e.target.value ? e.target.value.toUpperCase() : '';
    
    // Validate format
    const prefixError = validateInvoicePrefix(prefix);
    if (prefixError) {
      setError(prefixError);
      return;
    }

    try {
      // Check if invoice prefix is already in use
      const { data: existingPrefix, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('invoice_prefix', prefix)
        .not('id', 'eq', user?.id)
        .single();

      if (existingPrefix) {
        setError('User Invoice Prefix is already in use');
        return;
      }

      // Update the invoice prefix
      const { error } = await supabase
        .from('profiles')
        .update({ invoice_prefix: prefix })
        .eq('id', user?.id);

      if (error) throw error;

      setSuccess('Invoice prefix updated successfully');
      setProfileData(prev => ({
        ...prev,
        invoice_prefix: prefix
      }));
    } catch (error: any) {
      setError(error.message);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: 1200, 
      mx: 'auto', 
      p: isMobile ? 1.5 : 4
    }}>
      {/* Header Section - Responsive typography */}
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          sx={{ 
            fontWeight: 800,
            mb: isMobile ? 0.5 : 1,
            background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            fontSize: isMobile ? '1.75rem' : '2.5rem',
            lineHeight: isMobile ? 1.2 : 1.3,
          }}
        >
          Profile Management
        </Typography>
        <Typography 
          variant={isMobile ? "body1" : "h6"} 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: isMobile ? '0.875rem' : undefined
          }}
        >
          Manage your personal information and settings
        </Typography>
      </Box>

      {/* Alerts - Adjust for mobile */}
      {(error || success) && (
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          {error && (
            <Alert 
              severity="error" 
              variant="filled"
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  fontWeight: 500
                }
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert 
              severity="success"
              variant="filled"
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  fontWeight: 500
                }
              }}
            >
              {success}
            </Alert>
          )}
        </Box>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Profile Photo Section - Full width on mobile */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ 
                mb: isMobile ? 2 : 3, 
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : undefined
              }}>
                Profile Photo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: isMobile ? 1 : 2 
              }}>
                <Avatar
                  src={profileData.avatar_url || undefined}
                  alt={profileData.username}
                  sx={{ 
                    width: isMobile ? 100 : 120, 
                    height: isMobile ? 100 : 120,
                    bgcolor: theme.palette.primary.main,
                    border: '4px solid white',
                    boxShadow: theme.shadows[3]
                  }}
                >
                  {!profileData.avatar_url && profileData.username?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mt: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto',
                  alignItems: 'center'
                }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleAvatarUpload}
                  />
                  <label htmlFor="avatar-upload" style={{ width: isMobile ? '100%' : 'auto' }}>
                    <Button
                      variant="contained"
                      component="span"
                      fullWidth={isMobile}
                      startIcon={<PhotoCameraIcon />}
                      sx={{ 
                        borderRadius: 2,
                        bgcolor: theme.palette.primary.main,
                        py: isMobile ? 1 : undefined,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        }
                      }}
                    >
                      Upload Photo
                    </Button>
                  </label>
                  {profileData.avatar_url && (
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      color="error"
                      onClick={handleRemoveAvatar}
                      sx={{ 
                        borderRadius: 2,
                        py: isMobile ? 1 : undefined
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Information Section - Full width on mobile */}
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ 
                mb: isMobile ? 2 : 3, 
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : undefined
              }}>
                Profile Information
              </Typography>
              <form onSubmit={handleProfileSubmit}>
                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Username"
                      name="username"
                      value={profileData.username || ''}
                      onChange={handleProfileChange}
                      fullWidth
                      required
                      disabled
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Mobile No."
                      name="mobile_no"
                      value={profileData.mobile_no || ''}
                      onChange={handleProfileChange}
                      fullWidth
                      required
                      disabled
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <NumbersIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      name="email"
                      value={profileData.email || ''}
                      disabled
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Branch"
                      name="branch"
                      value={profileData.branch || ''}
                      disabled
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Role"
                      name="role"
                      value={profileData.role || ''}
                      disabled
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Invoice Prefix"
                      name="invoice_prefix"
                      value={profileData.invoice_prefix || ''}
                      onChange={handleProfileChange}
                      disabled
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <VpnKeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password Section */}
        <Grid item xs={12}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ 
                mb: isMobile ? 2 : 3, 
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : undefined
              }}>
                Change Password
              </Typography>
              <form onSubmit={handlePasswordSubmit}>
                <Grid container spacing={isMobile ? 2 : 3}>
                  {/* Stack password fields vertically on mobile */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Current Password"
                      name="currentPassword"
                      type="password"
                      value={passwordData.currentPassword || ''}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="New Password"
                      name="newPassword"
                      type="password"
                      value={passwordData.newPassword || ''}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword || ''}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth={isMobile}
                      sx={{ 
                        mt: isMobile ? 1 : 2,
                        height: isMobile ? 40 : 48,
                        borderRadius: 24,
                        px: isMobile ? 2 : 4,
                        backgroundColor: '#4285f4',
                        boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)',
                        '&:hover': {
                          backgroundColor: '#3367d6'
                        }
                      }}
                    >
                      Update Password
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileManagement; 